/* preco/ — o painel de precificação: a resposta que o usuário veio buscar.
 *
 * DIVISÃO DE TRABALHO, e ela é a regra mais importante desta pasta:
 *
 *   @favo/core decide          esta tela mostra
 *   ─────────────────────      ───────────────────────────────────────────
 *   montarParametros           os campos, já resolvidos (override → config)
 *   cenario                    indicadores, cascata, DRE
 *   recomendar                 o veredito, em cinco estados
 *
 * Nenhuma conta de margem, preço ou rateio nasce aqui. A aritmética que
 * existe é de exibição: decompor o que o motor entregou junto (custo de
 * serviço = CDT menos kit), levar um valor por projeto para o mês
 * (× projetos/mês, o mesmo fator do motor) e posicionar um número numa régua.
 *
 * QUEM MANDA NO NÚMERO: o servidor. `useAnalise` é um GET que roda o MESMO
 * motor sobre as linhas do banco. Enquanto o usuário digita, a tela calcula
 * localmente para responder na velocidade do teclado; quando não há edição
 * pendente nem envio em voo, o vetor de parâmetros exibido passa a ser o do
 * servidor. É por isso que o relatório nunca discorda do painel.
 *
 * O desenho é o da Colmeia, e vem inteiro do design system: célula, campo de
 * cor, medidor de nível, favo de cem células, régua de indicador. Esta tela
 * não escreve cor, raio nem tamanho de fonte.
 *
 * UM MÓDULO POR CAPÍTULO da tela: contexto, veredito, indicadores, entradas,
 * cascata, dre, comparativo. `salvamento.ts` é como a tela grava;
 * `grandezas.ts` e `cabecalho.tsx` são o que dois capítulos diriam igual.
 * Este index só decide O QUE mostrar. */

import { cenario, montarParametros, recomendar } from '@favo/core';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Celula } from '@/componentes/celula';
import { Button } from '@/componentes/ui/button';
import {
  mensagemDoErro,
  useAnalise,
  useSalvarOrcamento,
  type Cenario,
  type Parametros,
  type Recomendacao,
} from '@/dados';
import { Pagina } from '@/layout/pagina';

import { Cabecalho } from './cabecalho';
import { Cascata } from './cascata';
import { Comparativo } from './comparativo';
import { Contexto } from './contexto';
import { DreDoProjeto } from './dre';
import { Entradas } from './entradas';
import { Indicadores } from './indicadores';
import { useSalvamento } from './salvamento';
import { Veredito } from './veredito';

export function Preco() {
  const salvarOrcamento = useSalvarOrcamento();
  const s = useSalvamento();
  const { integrador, projeto, projetoId } = s;

  // ── Os números ───────────────────────────────────────────────────────────
  const analise = useAnalise(projetoId ?? null, Boolean(s.integradorBanco));

  const local = montarParametros({ integrador, projeto });
  // O servidor manda quando não há nada em trânsito. Enquanto houver, vale o
  // cálculo local: é ele que responde na velocidade do teclado.
  const confirmado =
    !s.pendente &&
    s.emVoo === 0 &&
    !analise.isFetching &&
    !analise.isPlaceholderData &&
    !!analise.data;
  const p: Parametros = confirmado ? (analise.data?.parametros ?? local) : local;

  const rec: Recomendacao = recomendar(p);

  // `null`, e não um cenário fabricado sobre `p.ticket`.
  //
  // Quando o motor recusa opinar, `p.ticket` é a média dos projetos da
  // sazonalidade, não o preço deste projeto. Reconstruir o cenário sobre ela
  // deixava cascata, DRE e comparativo desenhando com precisão um preço que
  // ninguém digitou: o veredito calava e o resto da tela continuava afirmando.
  const atual: Cenario | null = 'atual' in rec ? rec.atual : null;

  // Sem preço é sem preço DESTE PROJETO. O ticket pode estar preenchido pela
  // média da empresa e ainda assim não haver preço a auditar.
  const semPreco = p.ticketOrigem !== 'projeto';
  const semVolume = !(p.V12 > 0);

  // ── Ações ────────────────────────────────────────────────────────────────
  function adotarSugerido(): void {
    // Os dois estados de preço baixo oferecem o botão, e os dois carregam
    // `precoSugerido`. Listar só um deixaria o botão do outro clicável e mudo.
    if (rec.estado !== 'abaixo' && rec.estado !== 'prejuizo') return;
    s.editarProjeto({ valor_venda: rec.precoSugerido });
    // Decisão explícita do usuário não espera debounce.
    s.empurrar();
  }

  async function salvarAnalise(): Promise<void> {
    await s.descarregar();
    try {
      await salvarOrcamento.mutateAsync({
        projeto_id: projetoId ?? null,
        // Fotografia: o que entrou e o que saiu, na semântica do motor.
        // Percentual vai em FRAÇÃO, como sai de @favo/core. O histórico lê
        // estas chaves; mudar nome aqui é mudar contrato lá.
        entradas: {
          nome_projeto: projeto?.nome_projeto ?? null,
          kwp: p.kwp,
          mods: p.mods,
          kit: p.kit,
          cdt: p.cdt,
          comissao: p.comissao,
          comissaoBase: p.comissaoBase,
          tributo: p.tributo,
          tributoBase: p.tributoBase,
          outras: p.outras,
          despFixa: p.despFixa,
          lucroAlvo: p.lucroAlvo,
        },
        resultados: {
          estado: rec.estado,
          ticket: p.ticket,
          precoSugerido: 'precoSugerido' in rec ? rec.precoSugerido : null,
          // Sem cenário não há resultado a arquivar. Gravar o número da média
          // da empresa aqui contaminaria o histórico com uma linha que parece
          // deste projeto e não é.
          mcUnit: atual?.mcUnit ?? null,
          mcPct: atual?.mcPct ?? null,
          lucroUnit: atual?.lucroUnit ?? null,
          lucroPct: atual?.lucroPct ?? null,
          lucroAnual: atual?.anual.lucro ?? null,
          V12: p.V12,
        },
      });
      toast.success('Análise salva.');
    } catch (erro) {
      toast.error(mensagemDoErro(erro));
    }
  }

  return (
    <Pagina
      titulo="Preço sugerido"
      descricao="O piso de preço que entrega a margem alvo, e o que acontece com cada real dele."
      etapa="preco"
      semIdentidade
      acoes={
        <>
          <Button
            variant="outline"
            onClick={() => void salvarAnalise()}
            disabled={salvarOrcamento.isPending || s.semEmpresa}
          >
            {salvarOrcamento.isPending ? 'Salvando' : 'Salvar análise'}
          </Button>
          <Button onClick={() => void s.irPara('/relatorio')} disabled={s.semEmpresa}>
            Gerar relatório
          </Button>
        </>
      }
    >
      {s.carregando ? (
        <p className="text-tinta-2 text-apoio" role="status">
          Carregando a análise.
        </p>
      ) : (
        <div className="favo">
          <Contexto
            integrador={integrador}
            projeto={projeto}
            p={p}
            emVoo={s.emVoo}
            pendente={s.pendente}
            confirmado={confirmado}
            erroDoServidor={analise.isError}
            aoNavegar={s.aoNavegar}
          />

          {/* Campo mata: o que fica e o que se decide. Uma vez por tela. */}
          <Veredito
            rec={rec}
            p={p}
            semEmpresa={s.semEmpresa}
            semProjeto={s.semProjeto}
            aoAdotar={adotarSugerido}
            aoNavegar={s.aoNavegar}
          />

          <Indicadores rec={rec} p={p} atual={atual} semPreco={semPreco} semVolume={semVolume} />

          {atual === null ? null : (
            <Celula como="section" assenta atraso={400}>
              <Cabecalho
                titulo="Atual × Sugerido"
                nota={
                  rec.estado === 'abaixo' || rec.estado === 'prejuizo'
                    ? 'A coluna Sugerido existe porque o preço está abaixo do mínimo. Ela mostra o mesmo projeto vendido no piso da meta.'
                    : 'Sem coluna Sugerido: o preço já alcança o mínimo para a meta, e o sistema não sugere reduzir preço.'
                }
              />
              <Comparativo rec={rec} p={p} atual={atual} />
            </Celula>
          )}

          <div className="favo @painel-largo:grid-cols-[minmax(0,1.62fr)_minmax(0,1fr)]">
            {atual === null ? null : (
              <Cascata p={p} atual={atual} semPreco={semPreco} aoNavegar={s.aoNavegar} />
            )}
            <DreDoProjeto p={p} atual={atual} semPreco={semPreco} />
          </div>

          <Entradas
            p={p}
            atual={atual}
            integrador={integrador}
            projeto={projeto}
            revisao={s.revisao}
            semEmpresa={s.semEmpresa}
            semProjeto={s.semProjeto}
            semVolume={semVolume}
            editarProjeto={s.editarProjeto}
            editarIntegrador={s.editarIntegrador}
            aoNavegar={s.aoNavegar}
          />

          {/* ── RODAPÉ ───────────────────────────────────────────────────── */}
          <Celula
            campo="areia"
            respiro="justo"
            assenta
            atraso={440}
            className="flex flex-wrap items-center gap-4"
          >
            <span className="rotulo shrink-0">Premissas</span>
            <p className="apoio-nota min-w-80 flex-1">
              Comissão e tributo incidem sobre a venda total ou só sobre o serviço, conforme o
              cadastro em Despesas. A despesa fixa é rateada por volume: despesa fixa anual dividida
              por projetos no ano. Os valores mensais são a média anual dividida por 12.
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link to="/custos" onClick={s.aoNavegar('/custos')}>
                Estrutura de custos
              </Link>
            </Button>
            <Button variant="outline" size="sm" asChild>
              <Link to="/historico" onClick={s.aoNavegar('/historico')}>
                Histórico
              </Link>
            </Button>
          </Celula>
        </div>
      )}
    </Pagina>
  );
}
